import {DataTableColumn} from 'mantine-datatable';
import {I18n} from 'aws-amplify/utils';
import {NavLink} from 'react-router-dom';
import {
    ActionIcon,
    Group,
    Text,
} from '@mantine/core';
import {AiOutlineLink} from 'react-icons/ai';

import {formatTimestamp} from '../../../utils/TableTimestamps';
import {Registrar} from '../types';

// FNP-1292 — THE COLUMNS, as `DataTableColumn<Registrar>[]`.
//
// ⚠️ EVERY COLUMN CARRIES AN `accessor`, and that is the whole reason this file
// replaced `tables/columns.tsx`. The old config was `{title, render}` only:
// `render` says how a cell LOOKS, `accessor` says what sorting sorts BY. A
// render-only config reads as complete and makes sorting impossible without
// touching every column, which is why the checklist puts it in the root-cause
// box rather than under sorting.
//
// ⚠️ NO COLUMN CARRIES AN `fr` WIDTH. `mantine-datatable@7.17.1` applies a
// column width as an INLINE STYLE (`width`/`minWidth`/`maxWidth` on the cell),
// and a CSS Grid fraction is not valid there — the browser discards it. A
// flexible column is one with NO width, which is what `name` and `website` get.
// See `src/features/zones/tests/README-column-widths.md`.

export const useRegistrarsTableColumns = () => {

    const columns: DataTableColumn<Registrar>[] = [
        {
            accessor: 'name',
            sortable: true,
            title: I18n.get('Name'),
            // The row's front door. Registrars have a detail page and the name
            // is how an operator reaches it.
            render: (item: Registrar) => (
                <NavLink to={`/registrars/${item.id}`}>{item.name ?? '—'}</NavLink>
            ),
        },
        {
            accessor: 'website',
            sortable: true,
            title: I18n.get('Website'),
            // ⚠️ The external link is an ActionIcon with an aria-label, not a
            // bare icon: it opens a third-party site in a new tab and a
            // keyboard user needs to know which registrar's site that is.
            render: (item: Registrar) => {
                if (!item.website) {
                    return <Text size={'sm'} c={'dimmed'}>—</Text>;
                }
                return (
                    <Group gap={'xs'} wrap={'nowrap'}>
                        <Text size={'sm'}>{item.website}</Text>
                        <ActionIcon
                            variant={'subtle'}
                            aria-label={`${I18n.get('Open website for')} ${item.name ?? item.website}`}
                            onClick={() => window.open(item.website, '_blank', 'noopener,noreferrer')}
                        >
                            <AiOutlineLink/>
                        </ActionIcon>
                    </Group>
                );
            },
        },
        {
            accessor: 'created',
            sortable: true,
            title: I18n.get('Created'),
            width: '180px',
            render: (item: Registrar) => (
                <Text size={'sm'}>{item.created ? formatTimestamp(item.created) : '—'}</Text>
            ),
        },
    ];

    return {
        columns,
    };
};
